import { nextWorkingDay, type ScheduleBucket } from "./schedule";

const NO_HOLIDAYS: ReadonlySet<string> = new Set();
const DAY_MS = 86_400_000;

/** Working days (weekends only) from the first working day on or after `start` to `end`, both included. */
export function workingDaysInWindow(start: Date, end: Date): number {
  let n = 0;
  for (let t = nextWorkingDay(start, NO_HOLIDAYS).getTime(); t <= end.getTime(); t += DAY_MS) {
    const dow = new Date(t).getUTCDay();
    if (dow !== 0 && dow !== 6) n++;
  }
  return n;
}

/** How many working days the template takes as written: the last day any bucket or card reaches. */
export function templateLength(buckets: ScheduleBucket[]): number {
  let longest = 0;
  for (const b of buckets) {
    longest = Math.max(longest, b.startDay + b.durationDays);
    for (const c of b.cards) {
      if (c.dueDay !== null) longest = Math.max(longest, (c.dueBase === "PROJECT" ? 0 : b.startDay) + c.dueDay + 1);
    }
  }
  return longest;
}

/**
 * Stretches or squeezes the template to fit `days` working days, keeping the order. Every bucket and
 * card keeps at least one working day; relative offsets are scaled by the same factor.
 */
export function fitTemplate<T extends ScheduleBucket>(buckets: T[], days: number): T[] {
  const natural = templateLength(buckets);
  if (natural === 0 || days < 1) return buckets;
  const f = days / natural;
  const scale = (n: number) => Math.round(n * f);
  return buckets.map((b) => {
    const startDay = Math.min(scale(b.startDay), days - 1);
    const endDay = Math.min(Math.max(scale(b.startDay + b.durationDays), startDay + 1), days);
    const durationDays = endDay - startDay;
    const within = (base: "PROJECT" | "BUCKET", n: number) => (base === "PROJECT" ? Math.min(scale(n), days - 1) : Math.min(scale(n), durationDays - 1));
    return {
      ...b,
      startDay,
      durationDays,
      cards: b.cards.map((c) => ({
        ...c,
        startDay: c.startDay === null ? null : within(c.startBase, c.startDay),
        dueDay: c.dueDay === null ? null : within(c.dueBase, c.dueDay),
      })),
    };
  });
}

